import { FormProvider, useForm } from "react-hook-form";
import DialogComponent from "../../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import { CreateCustomerContractForm } from "./CreateCustomerContractForm";
import { useAppDispatch } from "../../../../app/hooks";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";
import { AutocompleteType } from "../../../../common/types/autoComplete";
import { createCustomerContract } from "../../customerContractsSlice";
import { EezyCompany } from "../../../invoices/invoiceAPI";
import useIsLoading from "../../../../hooks/useIsLoading";

type Props = {
  customerCompany: AutocompleteType;
  open: boolean;
  onClose: () => void;
};

type FormValueTypes = {
  customerCompany: AutocompleteType;
  eezyCompany: EezyCompany | null;
  salesPerson: AutocompleteType | null;
  schedulingClass: AutocompleteType | null;
  monthlyWorkSchedulingClass: AutocompleteType | null;
};

export const CreateCustomerContractDialog = ({
  customerCompany,
  open,
  onClose: handleClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const isLoading = useIsLoading(["customerContracts"]);
  const { t } = useTranslation("customers");

  const defaultValues = {
    customerCompany: { id: customerCompany.id, name: customerCompany.name },
    eezyCompany: null,
    salesPerson: null,
    schedulingClass: null,
    monthlyWorkSchedulingClass: null,
  };

  const methods = useForm<FormValueTypes>({
    defaultValues,
    mode: "onChange",
  });

  const {
    formState: { isValid, isSubmitting },
  } = methods;

  const saveDisabled = !isValid || isSubmitting || isLoading;

  const onClose = () => {
    methods.reset();
    handleClose();
  };

  const onSave = (data: FormValueTypes) => {
    if (
      !data.eezyCompany ||
      !data.salesPerson ||
      !data.schedulingClass ||
      !data.monthlyWorkSchedulingClass
    ) {
      return;
    }
    const payload = {
      customerCompanyId: data.customerCompany.id,
      eezyCompanyId: data.eezyCompany.eezyCompanyId,
      salesPersonId: data.salesPerson.id,
      schedulingClassId: data.schedulingClass.id,
      monthlyWorkSchedulingClassId: data.monthlyWorkSchedulingClass.id,
      eezyCompanyName: data.eezyCompany.name,
      eezyCompanyBusinessId: data.eezyCompany.businessId,
    };
    dispatch(createCustomerContract(payload)).then((action) => {
      if (isFulfilled(action)) {
        onClose();
      }
    });
  };

  return (
    <DialogComponent open={open} onClose={onClose} sx={{ maxWidth: "640px" }}>
      <FormProvider {...methods}>
        <DialogContentWrapper
          title={t("customerContractInfo")}
          onSave={methods.handleSubmit(onSave)}
          onCancel={onClose}
          saveDisabled={saveDisabled}
          customSaveButtonLabel={t("common:save")}
        >
          <CreateCustomerContractForm />
        </DialogContentWrapper>
      </FormProvider>
    </DialogComponent>
  );
};
